/* eslint-disable no-param-reassign */
import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { coverageDuplicationUtils } from '@aet/components';
import { Question } from './MCQAssignments';
import {
  buildChapterToPageMapping,
  filterMCQsForChapter,
  createAssignmentObject,
  findNodeTitleById,
  findParentChapterForPage,
  areAllPagesSelected,
  getTitleGeneratedFlag,
  getSyllabusScheduleExcludedTypes
} from '../utils/AssignmentUtils';
import {
  AET_STORE_EVENTS, SYLLABUS_PERSISTENCE_ENABLED
} from '../common/Constants';
import CommonUtils from '../../../common/utils/CommonUtils';
import {
  saveSyllabusToIndexedDB,
  loadSyllabusFromIndexedDB,
  clearSyllabusFromIndexedDB
} from '../utils/SyllabusIDBUtils';

function appendUniqueTitle(titles, title) {
  if (title && !titles.includes(title)) {
    titles.push(title);
  }
}

function getTitlesForChapter(chapterId, pageIds, sectionIds, tocData, chapterToPageIdMap) {
  const chapterPages = chapterToPageIdMap.get(chapterId) || [];
  const selectedPages = pageIds.filter(id => chapterPages.includes(id));
  const chapterSections = sectionIds.filter(
    id => findParentChapterForPage(id, tocData) === chapterId
  );

  const leafPageIds = selectedPages.length > 0
    ? selectedPages
    : coverageDuplicationUtils.collectLeafPageIdsFromSectionIds(chapterSections, tocData);
  const titleNodeIds = selectedPages.length > 0 ? selectedPages : chapterSections;

  if (leafPageIds.length === 0 && titleNodeIds.length === 0) {
    const chapterTitle = findNodeTitleById(chapterId, tocData);

    return chapterTitle ? [chapterTitle] : [];
  }
  if (areAllPagesSelected(chapterId, leafPageIds, chapterToPageIdMap)) {
    const chapterTitle = findNodeTitleById(chapterId, tocData);

    return chapterTitle ? [chapterTitle] : [];
  }

  return titleNodeIds.map(id => findNodeTitleById(id, tocData)).filter(Boolean);
}

export function getTitlesForAssignment(textbookCoverage, tocData, assignmentTitle) {
  const sectionIds = (textbookCoverage?.sectionIds || []).filter(Boolean);
  const pageIds = textbookCoverage?.pages || [];
  const chapters = textbookCoverage?.chapters || [];

  // Fallback if no TOC data or chapters
  if (chapters.length === 0 && !assignmentTitle) {
    return { titles: [] };
  }
  // If no TOC data, try to extract titles directly from chapters
  if (!tocData) {
    const titles = chapters.map(ch => ch.title || ch.chapterTitle).filter(Boolean);
    // final fallback - use the syllabus title itself to make the berlin call
    if (titles.length === 0 && assignmentTitle) {
      return { titles: [assignmentTitle] };
    }
    return { titles };
  }

  const chapterToPageIdMap = buildChapterToPageMapping(tocData);
  const titles = [];

  const uniqueChapterIds = [...new Set([
    ...chapters.map(ch => ch.chapterId || ch.id).filter(Boolean),
    ...sectionIds.map(id => findParentChapterForPage(id, tocData)).filter(Boolean)
  ])];

  uniqueChapterIds.forEach((chapterId) => {
    getTitlesForChapter(chapterId, pageIds, sectionIds, tocData, chapterToPageIdMap)
      .forEach(title => appendUniqueTitle(titles, title));
  });

  // Fallback scenario , if titles empty still, old logic
  if (titles.length === 0 && chapters.length > 0) {
    chapters.forEach((ch) => {
      appendUniqueTitle(
        titles,
        findNodeTitleById(ch.chapterId || ch.id, tocData) || ch.title || ch.chapterTitle
      );
    });
  }
  // final fallback - use the syllabus title itself to make the berlin call
  if (titles.length === 0 && assignmentTitle) {
    appendUniqueTitle(titles, assignmentTitle);
  }

  return { titles };
}

const School = types.model({
  id: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  shortName: types.maybeNull(types.string)
});

const ChapterSection = types.model({
  num: types.maybeNull(types.string),
  title: types.maybeNull(types.string)
});

const Chapters = types.union(
  // Full chapter data (for MCQ mapping)
  types.model({
    chapterId: types.maybeNull(types.string),
    chapterTitle: types.maybeNull(types.string),
    num: types.maybeNull(types.number),
    title: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    id: types.maybeNull(types.string),
    selectedNodeId: types.maybeNull(types.string),
    selectedNodeType: types.maybeNull(types.string),
    sections: types.optional(types.maybeNull(types.array(ChapterSection)), null)
  }),
  // Minimal chapter data - just num and title
  types.model({
    num: types.maybeNull(types.number),
    title: types.maybeNull(types.string),
    selectedNodeId: types.maybeNull(types.string),
    selectedNodeType: types.maybeNull(types.string),
    sections: types.optional(types.maybeNull(types.array(ChapterSection)), null)
  }),
  // Any other structure
  types.frozen()
);

const Textbook = types.union(
  types.model({
    id: types.maybeNull(types.string),
    isbn: types.maybeNull(types.string),
    title: types.maybeNull(types.string), // Can be null as in your data
    edition: types.maybeNull(types.number),
    author: types.maybeNull(types.string),
    authors: types.maybeNull(types.string), // Alternative authors field
    publisher: types.maybeNull(types.string),
    source: types.maybeNull(types.string), // For "channels", "manual", etc.
    year: types.maybeNull(types.number), // Publication year
    pages: types.maybeNull(types.number), // Number of pages
    pearsonIds: types.optional(types.array(types.string), []),
    cover: types.maybeNull(types.string),
    coverUrl: types.maybeNull(types.string) // Alternative cover field
  }),
  types.model({
    title: types.maybeNull(types.string)
  }),
  types.frozen(),
  types.null
);

const Course = types.union(
  types.model({
    id: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    code: types.maybeNull(types.string),
    courseCode: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    credits: types.maybeNull(types.number),
    department: types.maybeNull(types.string)
  }),
  types.model({
    title: types.maybeNull(types.string)
  }),
  types.frozen(),
  types.null
);

const Professor = types.model({
  id: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  email: types.maybeNull(types.string)
});

const TextbookCoverage = types.union(
  types.model({
    chapters: types.maybeNull(types.array(Chapters)),
    sections: types.maybeNull(types.array(types.string)),
    sectionIds: types.optional(types.maybeNull(types.array(types.string)), []), // Add sectionIds for TOC mapping
    pages: types.maybeNull(types.array(types.string)),
    topics: types.maybeNull(types.array(types.string)),
    startPage: types.maybeNull(types.number),
    endPage: types.maybeNull(types.number)
  }),
  types.frozen(),
  types.null
);

const ScheduleItem = types.model({
  type: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  weekNumber: types.maybeNull(types.number),
  date: types.maybeNull(types.string),
  textbookCoverage: types.maybeNull(TextbookCoverage),
  confidence: types.maybeNull(types.number),
  time: types.optional(types.string, ''),
  isTitleGenerated: types.optional(types.boolean, false),
  referenceId: types.maybeNull(types.string),
  timeZone: types.optional(types.maybeNull(types.string), null), // to send and receive field to/from child, not api
  validationErrors: types.optional(types.array(types.frozen()), []),
  isValid: types.optional(types.boolean, true),
  dateNormalizationAction: types.maybeNull(types.string),
  coverageDedupeAction: types.maybeNull(types.string)
});

const Activity = types.model({
  type: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  date: types.maybeNull(types.string),
  chapters: types.optional(types.array(types.number), [])
});

const LectureTime = types.model({
  confidence: types.maybeNull(types.number),
  days: types.maybeNull(types.string),
  professor: types.maybeNull(types.string),
  time: types.maybeNull(types.string),
  type: types.maybeNull(types.string)
});

const Year = types.model({
  label: types.maybeNull(types.string),
  confidence: types.maybeNull(types.number)
});

const Term = types.model({
  label: types.maybeNull(types.string),
  confidence: types.maybeNull(types.number)
});

const Assignment = types.model({
  assignmentId: types.maybeNull(types.string),
  assignmentName: types.maybeNull(types.string),
  startDate: types.maybeNull(types.string),
  dueDate: types.maybeNull(types.string),
  timeZone: types.optional(types.maybeNull(types.string), null), // to send and receive field to/from child, not api
  weekNumber: types.maybeNull(types.number),
  assignmentType: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  textbookCoverage: types.maybeNull(TextbookCoverage),
  questions: types.optional(types.array(Question), []),
  time: types.optional(types.string, ''),
  isTitleGenerated: types.optional(types.boolean, false),
  referenceId: types.maybeNull(types.string),
  validationErrors: types.optional(types.array(types.frozen()), []),
  isValid: types.optional(types.boolean, true),
  dateNormalizationAction: types.maybeNull(types.string),
  coverageDedupeAction: types.maybeNull(types.string)
});

const ChapterMCQMapping = types.model('ChapterMCQMapping', {
  chapterId: types.string,
  questions: types.array(Question)
});

const SyllabusData = types.model({
  school: types.maybeNull(School),
  textbook: types.maybeNull(Textbook),
  course: types.maybeNull(Course),
  professor: types.optional(
    types.union(types.array(Professor), types.null),
    []
  ),
  schedule: types.optional(
    types.union(types.array(ScheduleItem), types.null),
    []
  ),
  activities: types.optional(
    types.union(types.array(Activity), types.null),
    []
  ),
  year: types.maybeNull(Year),
  term: types.maybeNull(Term),
  lectureTime: types.optional(
    types.union(types.array(types.maybeNull(LectureTime)), types.null),
    []
  )
});

const Validation = types.model({
  is_syllabus: types.maybeNull(types.boolean),
  confidence: types.maybeNull(types.number),
  error_message: types.maybeNull(types.string)
});

const SyllabusInfo = types.model({
  data: types.maybeNull(SyllabusData),
  assignments: types.optional(
    types.union(types.array(Assignment), types.null),
    []
  ),
  originTenantId: types.maybeNull(types.string),
  validation: types.maybeNull(Validation),
  assignmentValidation: types.optional(types.frozen(), null),
  dateNormalizationSummary: types.maybeNull(types.frozen())
});

const Syllabus = types
  .model('Syllabus', {
    uploadUrl: types.maybeNull(types.string),
    id: types.maybeNull(types.string),
    syllabusInfo: types.maybeNull(SyllabusInfo),
    /** Incremented on TOC mapping push — child dedupes once per token bump. */
    coverageDedupeSyncToken: types.optional(types.number, 0),
    excludeTitles: types.optional(types.array(types.string), []),
    // Store MCQs temporarily until syllabus assignments are available
    originalMCQData: types.maybeNull(types.frozen()),
    // Cache the chapter-to-MCQ mapping for performance
    chapterMCQMapping: types.optional(types.array(ChapterMCQMapping), []),
    // Track when asset is loaded
    assetLoaded: types.optional(types.boolean, false),
    // this is needed to presist across reloads, we need to know if we are in upload or add manually
    loadedFromDB: types.optional(types.boolean, false)
  })
  .actions(self => ({
    // Save syllabus data to indexdb
    saveToLocalStorage() {
      if (!SYLLABUS_PERSISTENCE_ENABLED) return;
      const key = CommonUtils.generateSyllabusLocalStorageKey();
      if (!key || !self.syllabusInfo) return;
      // If assignments/schedule are empty, clear db instead of saving
      const scheduleLength = self.syllabusInfo?.data?.schedule?.length || 0;
      const assignmentsLength = self.syllabusInfo?.assignments?.length || 0;
      if (scheduleLength === 0 && assignmentsLength === 0) {
        self.clearFromLocalStorage();
        return;
      }
      try {
        const data = {
          uploadUrl: self.uploadUrl,
          id: self.id,
          syllabusInfo: getSnapshot(self.syllabusInfo)
        };
        saveSyllabusToIndexedDB(key, data);
      } catch (e) {
        console.warn('Failed to save syllabus to indexeddb:', e);
      }
    },

    // Load syllabus data from localStorage
    loadFromLocalStorage() {
      if (!SYLLABUS_PERSISTENCE_ENABLED) return;
      const key = CommonUtils.generateSyllabusLocalStorageKey();
      if (!key) return;
      // Use IndexedDB (async)
      loadSyllabusFromIndexedDB(key).then((storedData) => {
        if (storedData?.syllabusInfo) {
          self.setStoreData(storedData.uploadUrl, storedData.id, storedData.syllabusInfo, true);
        } else {
          // No data for this book - clear the store
          self.setStoreData();
        }
      });
    },

    // Clear syllabus data from IndexedDB
    clearFromLocalStorage() {
      if (!SYLLABUS_PERSISTENCE_ENABLED) return;
      const key = CommonUtils.generateSyllabusLocalStorageKey();
      if (!key) return;
      clearSyllabusFromIndexedDB(key);
    },
    setStoreData(uploadUrl = null, id = null, syllabusInfo = null, loadedFromDB = false) {
      self.uploadUrl = uploadUrl;
      self.id = id;
      self.syllabusInfo = syllabusInfo;
      self.loadedFromDB = loadedFromDB;
    },
    fetchMCQAssignments(bookId) {
      Framework.getEventManager().publish('MCQ_ASSIGNMENT_REQUESTED', {
        bookId
      });
    },

    // Listen for asset loaded event
    onAssetLoaded() {
      self.assetLoaded = true;
      // fixes issue where mcq does not remap on coursepill change
      self.chapterMCQMapping.clear();
      // Try to build mapping now that asset is loaded
      self.buildChapterMCQMapping();

      // If we have syllabus, try to create assignments
      if (self.syllabusInfo) {
        self.mapMCQsToAssignments();
      }
    },
    onAssignmentsGetCompleted(transformedAssignments) {
      const hasReturnedAssignments = transformedAssignments && transformedAssignments.length > 0;
      if (hasReturnedAssignments) {
        // must clear storage even if 1 actual assignment returned
        self.clearFromLocalStorage();
      }
      const isUploadFlow = self.loadedFromDB;
      if (isUploadFlow) {
        if (hasReturnedAssignments) {
          self.clearAssignmentsIfEmpty();
        } else if ((!transformedAssignments || transformedAssignments.length === 0)) {
          self.loadFromLocalStorage();
        }
        return;
      }
      // wipe the local schedule data once GET happens (irrespective of assignments count)
      if (self.syllabusInfo && (hasReturnedAssignments
        || (!transformedAssignments || transformedAssignments.length === 0))) {
        self.clearAssignmentsIfEmpty();
      }
      self.refreshExcludeTitles();
    },
    clearAssignmentsIfEmpty() {
      if (self.syllabusInfo) {
        // empty the current mobx store
        self.setStoreData();
      }
      // Also clear from indexeddb
      self.clearFromLocalStorage();
    },

    /**
     * Cached function to build chapter-to-MCQ mapping
     * when both asset and MCQs are available
     * called only once
     * @returns {void}
     */
    buildChapterMCQMapping() {
      if (self.chapterMCQMapping.length > 0) {
        return; // Already built
      }

      // Check if both asset and MCQs are ready
      if (!self.assetLoaded || !self.originalMCQData?.mcqQuestions?.length) {
        return; // Not ready yet
      }

      const asset = Framework.getStoreRegistry().getStore('asset');
      const mcqQuestions = self.originalMCQData.mcqQuestions;

      // Use existing utility to get chapter-to-page mapping
      const chapterToPageIdMap = buildChapterToPageMapping(getSnapshot(asset));

      // Build chapter-to-MCQ mapping using existing utilities
      const mappingArray = [];
      chapterToPageIdMap.forEach((pageIds, chapterId) => {
        const questionsForChapter = filterMCQsForChapter(mcqQuestions, pageIds);
        if (questionsForChapter.length > 0) {
          mappingArray.push({
            chapterId,
            questions: questionsForChapter
          });
        }
      });

      // Use applySnapshot - this will trigger observable updates!
      applySnapshot(self.chapterMCQMapping, mappingArray);
      // Emit event to notify container that mapping is ready
      Framework.getEventManager().publish('CHAPTER_MCQ_MAPPING_READY', {
        mapping: self.chapterMCQMapping,
        totalChapters: Object.keys(mappingArray).length,
        totalMCQs: Object.values(mappingArray).reduce((sum, chapter) => sum + chapter.questions.length, 0)
      });
    },
    // function to filter out only specific types from syllabus extraction
    filterEligibleAssignments(syllabusInfo) {
      if (!syllabusInfo) {
        return null;
      }
      const filteredInfo = { ...syllabusInfo };
      if (Array.isArray(filteredInfo.assignments) && filteredInfo.assignments.length > 0) {
        filteredInfo.assignments = filteredInfo.assignments.filter((assignment) => {
          // Defensive checks: assignment exists, has type property, and type is a string
          const assignmentType = assignment?.type || assignment?.assignmentType;
          if (!assignment || typeof assignmentType !== 'string') {
            console.warn('Invalid assignment object or missing type:', assignment);
            return false;
          }
          const excludedTypes = getSyllabusScheduleExcludedTypes();
          return !excludedTypes.includes(assignmentType.toLowerCase());
        });
      }

      // Filter schedule array if exists
      if (filteredInfo.data?.schedule
          && Array.isArray(filteredInfo.data.schedule)
          && filteredInfo.data.schedule.length > 0) {
        const filteredSchedule = filteredInfo.data.schedule.filter((scheduleItem) => {
          // Defensive checks: scheduleItem exists, has type property, and type is a string
          if (!scheduleItem || typeof scheduleItem.type !== 'string') {
            console.warn('Invalid schedule item or missing type:', scheduleItem);
            return false;
          }
          const excludedTypes = getSyllabusScheduleExcludedTypes();
          return !excludedTypes.includes(scheduleItem.type.toLowerCase());
        });

        filteredInfo.data = {
          ...filteredInfo.data,
          schedule: filteredSchedule,
          length: filteredSchedule.length
        };
      }

      return filteredInfo;
    },

    setSyllabusData(
      { uploadUrl, id, syllabusInfo },
      isFromLocal = false,
      runCoverageDedupeOnSync = false
    ) {
      self.uploadUrl = uploadUrl || null;
      self.id = id || null;

      if (runCoverageDedupeOnSync) {
        self.coverageDedupeSyncToken += 1;
      }

      if (syllabusInfo) {
        // Set isTitleGenerated for schedule items that have titles
        if (syllabusInfo.data?.schedule) {
          syllabusInfo.data.schedule = syllabusInfo.data.schedule.map(item => ({
            ...item,
            isTitleGenerated: item.referenceId ? item.isTitleGenerated : getTitleGeneratedFlag(item)
          }));
        }

        if (syllabusInfo.assignments) {
          syllabusInfo.assignments = syllabusInfo.assignments.map(item => ({
            ...item,
            isTitleGenerated: item.referenceId ? item.isTitleGenerated : getTitleGeneratedFlag(item)
          }));
        }
      }

      self.syllabusInfo = self.filterEligibleAssignments(syllabusInfo);

      // If we have stored MCQ data and asset is loaded, create assignments now
      if (self.originalMCQData && self.syllabusInfo && self.assetLoaded) {
        self.buildChapterMCQMapping();
        self.mapMCQsToAssignments();
      }
      // Save to indexeddb for persistence
      if (isFromLocal) {
        self.saveToLocalStorage();
      }
    },

    setMCQQuestionsForAssignments(mcqData) {
      // Store MCQ data for future use
      self.originalMCQData = mcqData;
      // fixes issue where mcq does not remap on coursepill change
      self.chapterMCQMapping.clear();

      // Try to build mapping if asset is already loaded
      if (self.assetLoaded) {
        self.buildChapterMCQMapping();

        // If syllabus exists, create assignments immediately
        if (self.syllabusInfo) {
          self.mapMCQsToAssignments();
        }
      }

      Framework.getEventManager().publish('MCQ_ASSIGNMENT_FETCHED', mcqData);
    },

    /**
     * actual function to map MCQs to assignments
     */
    mapMCQsToAssignments() {
      if (!self.syllabusInfo?.data?.schedule) {
        return;
      }

      // Ensure mapping is built (should already be done, but safe fallback)
      if (self.chapterMCQMapping.length === 0) {
        self.buildChapterMCQMapping();
      }

      if (self.chapterMCQMapping.length === 0) {
        return; // Still not ready
      }

      // Convert array back to object for easy lookup
      const mappingObject = {};
      self.chapterMCQMapping.forEach((chapter) => {
        mappingObject[chapter.chapterId] = chapter.questions;
      });

      // Use cached mapping to create assignments
      const schedule = self.syllabusInfo.data.schedule;
      const newAssignments = [];

      schedule.forEach((scheduleItem) => {
        const allMCQsForAssignment = [];

        // Get MCQs for this schedule item using cached mapping
        if (scheduleItem.textbookCoverage?.chapters) {
          scheduleItem.textbookCoverage.chapters.forEach((chapter) => {
            const chapterId = chapter.chapterId;
            const mcqsForChapter = mappingObject[chapterId] || [];
            allMCQsForAssignment.push(...mcqsForChapter);
          });
        }

        // Always create assignment - with or without MCQs (using existing utility)
        const newAssignment = createAssignmentObject(scheduleItem, allMCQsForAssignment);
        newAssignments.push(newAssignment);
      });

      // Apply new assignments
      if (newAssignments.length > 0) {
        applySnapshot(self.syllabusInfo.assignments, newAssignments);
      }
    },

    generateTitlesForAllAssignments(
      bookId,
      AET_BULK_ASSIGNMENT_TITLE_CREATION = {},
      onComplete = () => {},
      onError = () => {}
    ) {
      const assignmentsToProcessStore = self.syllabusInfo?.schedule
                                  || self.syllabusInfo?.data?.schedule
                                  || self.syllabusInfo?.assignments || [];
      if (!assignmentsToProcessStore || assignmentsToProcessStore.length === 0) {
        onComplete();
        return;
      }

      self.refreshExcludeTitles();
      const excludeTitles = [...self.excludeTitles];
      const titlesSet = [];
      const assignmentsToProcess = [...getSnapshot(assignmentsToProcessStore)];
      const renderAssignmentsToProcess = assignmentsToProcess.map((item, idx) => {
        const referenceId = `${String(idx)}-${new Date().getTime()}`;
        if (!item.isTitleGenerated && !item.referenceId) {
          const assetStore = Framework.getStoreRegistry().getStore('asset');
          const tocData = assetStore ? getSnapshot(assetStore) : null;
          const { titles } = getTitlesForAssignment(item?.textbookCoverage, tocData, item?.title);
          if (titles && titles.length > 0) {
            titlesSet.push({
              referenceId,
              titles
            });
          }
        } else if (item.title && !excludeTitles.includes(item.title)) {
          excludeTitles.push(item.title);
        }
        return {
          ...item,
          dueDate: item.dueDate || item.date,
          referenceId: !item.referenceId ? referenceId : item.referenceId,

          isTitleGenerated: item.isTitleGenerated && !!item.referenceId
        };
      });
      if (self.syllabusInfo?.data?.schedule) {
        applySnapshot(self.syllabusInfo.data.schedule, renderAssignmentsToProcess);
      }
      if (self.syllabusInfo?.assignments) {
        applySnapshot(self.syllabusInfo.assignments, renderAssignmentsToProcess);
      }

      if (titlesSet.length > 0
        && AET_BULK_ASSIGNMENT_TITLE_CREATION.graphId
        && AET_BULK_ASSIGNMENT_TITLE_CREATION.graphVersion) {
        Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_TITLE_REQUESTED, {
          bookId,
          titlesSet,
          excludeTitles,
          onComplete,
          onError,
          ...AET_BULK_ASSIGNMENT_TITLE_CREATION
        });
      } else {
        // No Berlin API call needed - call onComplete directly
        onComplete();
      }
    },

    onTitleGenerationCompleted(titleSet = []) {
      if (!titleSet || titleSet.length === 0) {
        return;
      }
      if (self.syllabusInfo.assignments.length === 0) {
        // if assignments array not there, we regenarate after title gen
        self.mapMCQsToAssignments();
      }
      let assignments = [];
      let schedule = [];
      if (self.syllabusInfo?.assignments && self.syllabusInfo.assignments.length > 0) {
        assignments = JSON.parse(JSON.stringify(getSnapshot(self.syllabusInfo.assignments)));
      }
      if (self.syllabusInfo?.data?.schedule && self.syllabusInfo.data.schedule.length > 0) {
        schedule = JSON.parse(JSON.stringify(getSnapshot(self.syllabusInfo.data.schedule)));
      }
      titleSet.forEach((item) => {
        const { title, referenceId } = item;
        if (!title || !referenceId) {
          return;
        }
        if (assignments.length > 0) {
          const assignmentIndex = assignments.findIndex(assignment => assignment.referenceId === referenceId);
          if (assignmentIndex !== -1) {
            assignments[assignmentIndex].title = title;
            assignments[assignmentIndex].assignmentName = title;
            assignments[assignmentIndex].isTitleGenerated = true;
          }
        }
        if (schedule.length > 0) {
          const scheduleIndex = schedule.findIndex(scheduleItem => scheduleItem.referenceId === referenceId);
          if (scheduleIndex !== -1) {
            schedule[scheduleIndex].title = title;
            // schedule[scheduleIndex].assignmentName = title;
            schedule[scheduleIndex].isTitleGenerated = true;
          }
        }
      });
      if (assignments.length > 0) {
        applySnapshot(self.syllabusInfo.assignments, assignments);
      }
      if (schedule.length > 0) {
        applySnapshot(self.syllabusInfo.data.schedule, schedule);
      }
      self.refreshExcludeTitles();
    },

    reset() {
      self.clearFromLocalStorage();
      applySnapshot(self, {
        uploadUrl: null,
        id: null,
        syllabusInfo: null,
        originalMCQData: null,
        chapterMCQMapping: [],
        assetLoaded: false,
        loadedFromDB: false,
        excludeTitles: []
      });
    }
  }))
  .actions(self => ({
    afterCreate() {
      // Listen for asset loaded event
      Framework.getEventManager().on('ASSET_FETCHED', self.onAssetLoaded);
      Framework.getEventManager().on(AET_STORE_EVENTS.ASSIGNMENT_GET_COMPLETED, self.onAssignmentsGetCompleted);
    },
    refreshExcludeTitles() {
      const assignmentStore = Framework.getStoreRegistry().getStore('assignment');

      if (!assignmentStore) {
        self.excludeTitles.clear();
        return;
      }

      const snapshot = getSnapshot(assignmentStore);

      const updatedExcludeTitles = snapshot?.assignments?.map(a => a.title)?.filter(Boolean) || [];

      self.excludeTitles.clear();
      self.excludeTitles.push(...updatedExcludeTitles);
    }
  }));

export default Syllabus;
